import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@core/environments/environment';
import {
  CreatePartner,
  PartnerKycDocumentListResponse,
  PartnerKycDocumentStatus,
  PartnerListResponse,
  PartnerResponse,
  UpdatePartner,
} from '@core/models/admin/partners';
import { Pagination } from '@core/models/pagination.model';
import { finalize, Observable } from 'rxjs';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root',
})
export class PartnerService {
  private http = inject(HttpClient);
  private loadingService = inject(LoadingService);

  BASE_URL = `${environment.ADMIN_BASE_URL}/v1/partner`;

  getPartners(
    data?: Pagination,
    filterQuery?: string
  ): Observable<PartnerListResponse> {
    const uniqueStateUrl = 'get-partners';
    this.loadingService.setLoading(uniqueStateUrl, true);
    let url = '';

    // the filter query carries its own pagination
    if (filterQuery) {
      url = `${this.BASE_URL}/get-partners?${filterQuery}`;
    } else {
      url = `${this.BASE_URL}/get-partners?take=${data?.size ?? 10}&skip=${data?.skip ?? 0}`;
    }

    return this.http
      .get<PartnerListResponse>(url)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  createPartner(partner: CreatePartner): Observable<PartnerResponse> {
    const uniqueStateUrl = 'create-partner';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const formData = new FormData();
    formData.append('name', partner.name);
    formData.append('email', partner.email);

    // the document (and its type) are optional on partner creation
    if (partner.document) {
      formData.append('file', partner.document);
      formData.append('document_type', partner.document_type ?? '');
    }

    return this.http
      .post<PartnerResponse>(`${this.BASE_URL}/create-partner`, formData)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  updatePartner(partner: UpdatePartner): Observable<PartnerResponse> {
    const uniqueStateUrl = 'update-partner';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post<PartnerResponse>(`${this.BASE_URL}/update-partner`, partner)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  deletePartner(partnerId: string): Observable<unknown> {
    const uniqueStateUrl = 'delete-partner';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .post(`${this.BASE_URL}/delete-partner`, { id: partnerId })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  addPartnerKycDocument(payload: {
    partner_id: string;
    document_type: string;
    document: File;
  }): Observable<unknown> {
    const uniqueStateUrl = 'add-partner-kyc-document';
    this.loadingService.setLoading(uniqueStateUrl, true);

    const formData = new FormData();
    formData.append('partner_id', payload.partner_id);
    formData.append('document_type', payload.document_type);
    formData.append('file', payload.document);

    return this.http
      .post(`${this.BASE_URL}/add-partner-kyc-document`, formData)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getPartnerKycDocuments(
    data?: Pagination,
    filterQuery?: string
  ): Observable<PartnerKycDocumentListResponse> {
    const uniqueStateUrl = 'get-partner-kyc-document-list';
    this.loadingService.setLoading(uniqueStateUrl, true);
    let url = '';

    // the filter query carries its own pagination
    if (filterQuery) {
      url = `${this.BASE_URL}/get-partner-kyc-document-list?${filterQuery}`;
    } else {
      url = `${this.BASE_URL}/get-partner-kyc-document-list?take=${data?.size ?? 10}&skip=${data?.skip ?? 0}`;
    }

    return this.http
      .get<PartnerKycDocumentListResponse>(url)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  updatePartnerKycDocumentStatus(payload: {
    document_id: string;
    status: PartnerKycDocumentStatus;
    rejection_reason?: string;
  }): Observable<unknown> {
    const uniqueStateUrl = 'update-partner-kyc-document-status';
    this.loadingService.setLoading(uniqueStateUrl, true);

    return this.http
      .patch(`${this.BASE_URL}/update-partner-kyc-document-status`, payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
}
